import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { SIDEBAR_ITEMS } from 'src/constants/user/userConstants';
import { getUser, getUserType } from 'src/utils/user/user';
import { useTheme } from 'src/contexts/ThemeContext';
import CustomSnackbar from 'src/components/UI/Alerts/customSnackbar';

// ── Inline sun/moon icons — zero extra dependencies ──
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
  </svg>
);

interface INavbarProps { }

const Navbar = (props: INavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const activeRoute = location.pathname;
  const user = getUser();
  const USER_TYPE = getUserType();
  const { theme, toggleTheme } = useTheme();

  const lngs = {
    en: { nativeName: 'English', flag: '🇬🇧' },
    ar: { nativeName: 'العربية', flag: '🇦🇪' },
  };

  const handleLogout = () => {
    window.localStorage.setItem('activeTab', '1');
    window.localStorage.removeItem('user');
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('selectedPort');
    window.sessionStorage.clear();
    navigate('/login');
  };

  const navigateProfile = () => {
    navigate('/profile');
  };

  const [selectedKey, setSelectedKey] = useState(() => {
    const activeItem = SIDEBAR_ITEMS[USER_TYPE]?.find(
      (item) => item.route === activeRoute && item.showInTabs !== false
    );
    return activeItem ? activeItem.id : undefined;
  });
  const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = useState(false);
  const [showLanguageToast, setShowLanguageToast] = useState(false);
  const [languageToastMessage, setLanguageToastMessage] = useState('');

  useEffect(() => {
    const activeItem = SIDEBAR_ITEMS[USER_TYPE]?.find(
      (item) => item.route === activeRoute && item.showInTabs !== false
    );
    if (activeRoute === '/profile') {
      setSelectedKey(undefined);
    } else if (activeItem) {
      setSelectedKey(activeItem.id);
    } else {
      setSelectedKey(undefined);
    }
  }, [activeRoute, USER_TYPE]);

  const handleTabChange = (key: string | number) => {
    const selectedItem = SIDEBAR_ITEMS[USER_TYPE]?.find(
      (item) => item.id === key
    );
    if (selectedItem && selectedItem.route) {
      navigate(selectedItem.route);
    }
  };

  const changeLanguage = async (toLang: string) => {
    localStorage.setItem('selectedLanguage', toLang);
    await i18n.changeLanguage(toLang);
    document.documentElement.dir = toLang === 'ar' ? 'rtl' : 'ltr';
    setIsLanguagePopoverOpen(false);
    setLanguageToastMessage(
      toLang === 'ar'
        ? t('nav_bar.language_changed_ar')
        : t('nav_bar.language_changed_en')
    );
    setShowLanguageToast(true);
  };

  return (
    <div
      className="w-full shadow-nav z-50 fixed top-0"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-light)',
        transition: 'background-color 0.25s ease, border-color 0.25s ease',
      }}
    >
      <CustomSnackbar
        open={showLanguageToast}
        handleClose={() => setShowLanguageToast(false)}
        message={languageToastMessage}
        severity="success"
        anchorOrigin={{ vertical: 'top', horizontal: 'start' }}
      />
      <div className="flex items-center px-6 justify-between">
        <div className="flex items-center">
          <div className="py-2">
            <img
              src="assets/images/murafiq_logo.png"
              alt="logo"
              className="h-14 cursor-pointer me-20 invert dark:invert-0"
              onClick={() => navigate('/')}
            />
          </div>
        </div>

        {/* Centered Tabs */}
        <div className="flex flex-grow justify-center">
          <Tabs
            aria-label="Navbar Tabs"
            variant="underlined"
            selectedKey={selectedKey}
            onSelectionChange={handleTabChange}
            classNames={{
              tabList: 'top-[2px] flex justify-center rounded-none p-0',
              cursor: selectedKey ? 'h-[3px] bg-[var(--color-primary)]' : 'bg-transparent',
              tab: `font-semibold max-w-fit h-20 text-[16px]`,
              tabContent: `transition-all duration-300 top-1 group-data-[selected=true]:text-[var(--color-primary)] text-[var(--text-muted)]`,
            }}
          >
            {(SIDEBAR_ITEMS[USER_TYPE] || [])
              .filter((item) => item.showInTabs !== false)
              .map((item) => (
                <Tab
                  key={item.id}
                  title={
                    <div className="flex items-center space-x-2">
                      <span className="capitalize">
                        {t(`nav_bar.${item.id}`)}
                      </span>
                    </div>
                  }
                />
              ))}
          </Tabs>
        </div>

        {/* Right controls */}
        <div className="flex items-center justify-end gap-3 me-2">
          {/* Language buttons */}
          <div className="col-span-2 my-auto">
            <Popover
              isOpen={isLanguagePopoverOpen}
              onOpenChange={setIsLanguagePopoverOpen}
              placement={i18n.language === 'ar' ? 'bottom-end' : 'bottom-start'}
              offset={8}
              classNames={{
                content: 'rounded-xl p-2',
              }}
            >
              <PopoverTrigger>
                <Button
                  variant="bordered"
                  isIconOnly
                  className="rounded-lg transition-all duration-200 text-xl"
                  style={{
                    backgroundColor: 'var(--bg-overlay)',
                    borderColor: 'var(--border-light)',
                    color: 'var(--text-main)',
                  }}
                >
                  {(lngs as any)[i18n.resolvedLanguage || 'en']?.flag || '🇬🇧'}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                style={{
                  backgroundColor:
                    theme === 'dark'
                      ? 'rgba(28, 37, 46, 0.9)'
                      : 'rgba(255, 255, 255, 0.9)',
                  border: `1px solid ${theme === 'dark'
                    ? 'rgba(148, 163, 184, 0.12)'
                    : 'rgba(15, 23, 42, 0.08)'
                    }`,
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow:
                    theme === 'dark'
                      ? '0 18px 40px rgba(15, 23, 42, 0.34)'
                      : '0 18px 40px rgba(15, 23, 42, 0.12)',
                }}
              >
                <div className="min-w-[180px]">
                  {Object.keys(lngs).map((lng) => {
                    const isActive = i18n.resolvedLanguage === lng;

                    return (
                      <button
                        key={lng}
                        type="button"
                        onClick={() => changeLanguage(lng)}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
                        style={{
                          backgroundColor: isActive
                            ? 'var(--bg-surface-2)'
                            : 'transparent',
                          color: 'var(--text-main)',
                        }}
                      >
                        <span className="text-2xl leading-none">
                          {(lngs as any)[lng].flag}
                        </span>
                        <span className="text-base font-normal">
                          {(lngs as any)[lng].nativeName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Theme toggle button */}
          <Button
            variant="bordered"
            isIconOnly
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="rounded-lg transition-all duration-200"
            style={{
              backgroundColor: 'var(--bg-overlay)',
              borderColor: 'var(--border-light)',
              color: 'var(--text-main)',
            }}
            onPress={toggleTheme}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </Button>

          {/* User avatar dropdown */}
          <Dropdown
            placement={i18n.language === 'ar' ? 'bottom-end' : 'bottom-start'}
            classNames={{
              base: 'before:bg-transparent',
              content:
                'py-1 px-1 rounded-2xl border border-[var(--border-light)] bg-[var(--bg-surface)] shadow-[var(--shadow-card)]',
            }}
            radius="md"
          >
            <DropdownTrigger>
              <Avatar
                isBordered
                as="button"
                className="transition-transform p-1"
                name=""
                size="md"
                showFallback
                classNames={{
                  base: 'bg-gradient-to-br from-[#0B9E8A] to-[#087A6A]',
                }}
              />
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Profile Actions"
              variant="flat"
              className="p-2"
              style={{ color: 'var(--text-main)' }}
            >
              <DropdownSection
                showDivider
                classNames={{
                  divider: 'bg-[var(--border-light)]',
                }}
              >
                <DropdownItem
                  key="profile"
                  className="h-16 gap-2"
                  onPress={navigateProfile}
                  color="secondary"
                  title={t('nav_bar.profile')}
                  description={user.email || null}
                  classNames={{
                    title: 'text-[16px] font-bold gap-2',
                    description: 'text-sm opacity-60',
                  }}
                  style={{ color: 'var(--text-main)' }}
                ></DropdownItem>
              </DropdownSection>
              <DropdownSection>
                <DropdownItem
                  key="logout"
                  onClick={handleLogout}
                  title={t('logout')}
                  color="danger"
                  classNames={{
                    title: 'text-[16px] font-bold gap-2 text-[var(--color-error)]',
                  }}
                ></DropdownItem>
              </DropdownSection>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
